/**
 * EL ADAPTADOR DE PEDIDOS. AQUÍ ESTÁ EL DINERO.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ESTE ES EL ÚNICO ARCHIVO QUE CONOCE LA FORMA DEL API DE PEDIDOS.         ║
 * ║  El contrato vive en `api/src/routes/orders.ts` + `services/orders.ts`.   ║
 * ║  `Checkout.js` y `MyOrders.js` siguen hablando de `unitPrice`, `total`,   ║
 * ║  `status:'pending'` y `deliveryMethod:'delivery'`, que son los nombres    ║
 * ║  que la app ya usa. Si el contrato cambia, se ajusta AQUÍ.                ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Mismo patrón que `catalog.js` y `vehicles.js`, y por el mismo motivo. Pero
 * esta pantalla se trata distinto que las otras dos, y conviene decir por qué:
 * en el catálogo un error se corrige recargando; aquí un error se devuelve en
 * efectivo. Y el fallo más caro no es que la compra falle — es que SALGA BIEN Y
 * PAREZCA QUE FALLÓ, porque entonces el cliente vuelve a pulsar y paga dos veces.
 *
 * ─── LAS SEIS TRAMPAS DE ESTA TRADUCCIÓN, POR LO QUE CUESTAN ────────────────
 *
 *  1. 🔴 LOS IMPORTES NO SE MANDAN. `OrderCreateSchema` es `.strict()`: un
 *     `unitPrice` no se ignora, devuelve 400 CON SU NOMBRE. Y el carrito de la
 *     pantalla está LLENO de importes (`unitPrice`, `installationPrice`,
 *     `productName`, `cartKey`, `photo`). Por eso el cuerpo se arma campo a
 *     campo, con una lista de PERMITIDOS, y jamás con un `...item`: un spread es
 *     una lista de prohibidos disfrazada, y falla al AÑADIR un campo al carrito.
 *  2. 🔴 `variationId` ES OPCIONAL, NO NULABLE (`z.string().optional()`). El
 *     código de hoy manda `i.variationId || null` porque a la callable de
 *     Firestore le da igual. Aquí un `null` es un 400 y la compra no ocurre.
 *  3. 🔴 LA CLAVE DE IDEMPOTENCIA LA ACUÑA EL CLIENTE, y es lo único que
 *     convierte un reintento en una pregunta ("¿qué pasó con aquello?") en vez
 *     de en una compra nueva. De dónde sale y cuánto vive se decide en
 *     `Checkout.js`, que es quien conoce la INTENCIÓN; aquí solo se acuña.
 *  4. LOS ENUMS. Postgres habla en MAYÚSCULAS ('PENDING', 'DELIVERY',
 *     'RECEIPT_SUBMITTED') y toda la app compara en minúsculas. Sin bajarlos,
 *     `STATUS_CONFIG[order.status]` sale `undefined`, el pedido aparece siempre
 *     como "Pendiente" y el rastreador se queda en el paso cero. Sin un error.
 *  5. EL DINERO EN CENTAVOS. `totalCents` no es `total`. Y un reembolso ausente
 *     NO es cero: cero afirma "no se devolvió nada", que es una frase distinta.
 *  6. LAS FECHAS. La app hace `createdAt.toDate()`. Un texto ISO no lo tiene.
 *
 * ─── UN CAMBIO QUE EL CLIENTE VE, Y ESTÁ ELEGIDO ────────────────────────────
 *
 * 🔴 LA SOBREVENTA PASA A SER UN RECHAZO (409). Hoy dos compras simultáneas del
 * último artículo pasan las dos, el disparador recorta el inventario a 0 y deja
 * una bandera que ninguna pantalla enseña: el segundo cliente cree que compró
 * algo que no existe y se entera cuando va a recogerlo. Con el API, la segunda
 * se rechaza. Alguien que HOY compraría, mañana recibe un "no". Es más honesto,
 * pero es un cambio de conducta, y por eso el mensaje tiene que decir QUÉ
 * producto y CUÁNTAS unidades quedan: el API lo dice en su mensaje y ese mensaje
 * se le enseña al cliente tal cual (`orderErrorReason`).
 */
import { apiFetch, ApiError } from './client';

/** El tope del API (`pageSize` máximo en `ListQuerySchema`). Pedir más es 400. */
export const ORDERS_PAGE_SIZE = 50;

/** Tope de profundidad del API. Pedir la 201 no sale vacía: es un 400. */
export const ORDERS_MAX_PAGE = 200;

/**
 * Lo que se rechaza ANTES de salir a la red, porque el viaje ya está perdido y
 * el mensaje que devolvería el servidor no sería más claro que este.
 */
export class OrderDraftError extends Error {
  constructor(message, code = 'invalid_draft') {
    super(message);
    this.name = 'OrderDraftError';
    this.code = code;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// PIEZAS SUELTAS
// ────────────────────────────────────────────────────────────────────────────

/**
 * Centavos → pesos. `null` se conserva como `null`.
 *
 * 🔴 Un importe ausente NO es cero. `Number(x) || 0` es el bug que el contrato
 * documenta como `CORRUPT_PRICE_THROWS_INSTEAD_OF_FREE`: convertía un dato que
 * faltaba en una línea GRATIS.
 */
function pesos(cents) {
  if (cents === undefined || cents === null) return null;
  const n = Number(cents);
  return Number.isFinite(n) ? n / 100 : null;
}

/** Lo mismo, pero para un importe que el API SIEMPRE manda (subtotal, total). */
function pesosOcero(cents) {
  const v = pesos(cents);
  return v === null ? 0 : v;
}

/**
 * El sello de tiempo con la MISMA superficie que un `Timestamp` de Firestore.
 * La app no llama a `new Date(x)`: llama a `x.toDate()`. Convertir en el borde
 * es exactamente para lo que existe un adaptador.
 *
 * Una fecha ilegible es `null`, nunca la época de Unix: un 1 de enero de 1970 en
 * el historial de compras es un dato falso, y encima uno que se pinta.
 */
function ts(iso) {
  if (iso === null || iso === undefined || iso === '') return null;
  if (iso && typeof iso.toDate === 'function') return iso;
  const ms = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return { toDate: () => new Date(ms), toMillis: () => ms };
}

/** 'READY_FOR_PICKUP' → 'ready_for_pickup'. Ver la trampa nº 4. */
function minusculas(v) {
  return v === null || v === undefined ? null : String(v).toLowerCase();
}

/** Texto con contenido, o `null`. */
function txt(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' ? null : s;
}

/** El método de entrega de la app → el enum de la base. */
const DELIVERY_METHOD_API = { delivery: 'DELIVERY', pickup: 'PICKUP', installation: 'INSTALLATION' };

// ────────────────────────────────────────────────────────────────────────────
// LA CLAVE DE IDEMPOTENCIA
// ────────────────────────────────────────────────────────────────────────────

/**
 * ACUÑA UNA CLAVE NUEVA. Una por INTENCIÓN de compra, no por petición.
 *
 * El API deriva el identificador del pedido de `(uid del token, esta clave)`, así
 * que dos peticiones con la MISMA clave crean UN solo pedido y la segunda
 * responde `created:false`. Esta función solo fabrica la cadena; QUIÉN la guarda
 * y CUÁNTO vive es la decisión de `Checkout.js`, y es la decisión importante.
 *
 * `crypto.randomUUID` cuando el navegador lo tiene (todos los que soporta la
 * app), y si no, reloj + azar. No hace falta que sea impredecible —no es un
 * secreto, y el uid del token entra en el resumen del servidor— solo que no se
 * repita entre dos intenciones distintas.
 */
export function newIdempotencyKey() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 8)}`;
}

// ────────────────────────────────────────────────────────────────────────────
// CREAR
// ────────────────────────────────────────────────────────────────────────────

/**
 * EL CUERPO QUE ESPERA EL API, a partir del carrito que la pantalla ya tiene.
 *
 * Lista de PERMITIDOS, campo a campo. Ver la trampa nº 1.
 */
export function orderCreateBody(draft = {}) {
  const clave = typeof draft.idempotencyKey === 'string' ? draft.idempotencyKey.trim() : '';
  if (clave.length < 8) {
    // Sin clave no hay barrera contra el cobro doble, y el API la exige. Salir a
    // la red sin ella es gastar un viaje para que nos digan justo esto.
    throw new OrderDraftError('idempotencyKey: hacen falta al menos 8 caracteres.', 'missing_key');
  }

  const metodo = DELIVERY_METHOD_API[String(draft.deliveryMethod || 'pickup').toLowerCase()];
  if (!metodo) {
    throw new OrderDraftError(`deliveryMethod: "${draft.deliveryMethod}" no es un método de entrega.`);
  }
  const entrega = metodo === 'DELIVERY';

  const lineas = Array.isArray(draft.items) ? draft.items : [];
  if (lineas.length === 0) throw new OrderDraftError('items: el pedido no lleva nada.', 'empty_cart');

  const items = lineas.map((i, n) => {
    const productId = txt(i.productId ?? i.id);
    if (!productId) {
      // Un carrito viejo de `localStorage` puede traer líneas sin producto. El
      // API contestaría 400; decirlo aquí evita el viaje y nombra la línea.
      throw new OrderDraftError(`items.${n}.productId: esa línea del carrito no tiene producto.`, 'bad_item');
    }
    const cantidad = Math.floor(Number(i.quantity));
    if (!Number.isFinite(cantidad) || cantidad < 1) {
      throw new OrderDraftError(`items.${n}.quantity: cantidad inválida.`, 'bad_item');
    }
    const linea = { productId, quantity: cantidad };
    // 🔴 Se OMITE cuando no hay. Un `null` es un 400 (trampa nº 2).
    const variacion = txt(i.variationId);
    if (variacion) linea.variationId = variacion;
    if (i.installationRequested === true) linea.installationRequested = true;
    return linea;
  });

  const body = {
    idempotencyKey: clave,
    workshopId: txt(draft.workshopId),
    paymentMethod: txt(draft.paymentMethod) || 'efectivo',
    deliveryMethod: metodo,
    items,
  };
  if (!body.workshopId) throw new OrderDraftError('workshopId: no se sabe a qué negocio se le compra.', 'no_workshop');

  // Solo con entrega a domicilio: el API guarda `null` en los otros casos de
  // todos modos, y mandarlo sería decirle al taller una dirección que no aplica.
  if (entrega) {
    const dir = txt(draft.deliveryAddress);
    if (dir) body.deliveryAddress = dir;
    if (Number.isFinite(Number(draft.deliveryLat)) && draft.deliveryLat !== null) body.deliveryLat = Number(draft.deliveryLat);
    if (Number.isFinite(Number(draft.deliveryLng)) && draft.deliveryLng !== null) body.deliveryLng = Number(draft.deliveryLng);
    if (Number.isFinite(Number(draft.deliveryGpsAccuracy)) && draft.deliveryGpsAccuracy !== null) {
      body.deliveryGpsAccuracy = Math.round(Number(draft.deliveryGpsAccuracy));
    }
  }

  const comprobante = txt(draft.receiptURL ?? draft.receiptUrl);
  if (comprobante) body.receiptUrl = comprobante;

  const notas = txt(draft.notes);
  if (notas) body.notes = notas;

  if (metodo === 'INSTALLATION') {
    const inst = draft.install;
    if (!inst || !txt(inst.date) || !txt(inst.time)) {
      // El API lo rechaza igual, y con razón: sin esto el cliente paga la
      // instalación y no queda citado, que es lo que hoy pasa en silencio.
      throw new OrderDraftError('install: una orden con instalación tiene que traer fecha y hora.', 'no_install');
    }
    body.install = { date: txt(inst.date), time: txt(inst.time) };
    const vehiculo = txt(inst.vehicleId);
    if (vehiculo) body.install.vehicleId = vehiculo;
    const nombre = txt(inst.vehicleName);
    if (nombre) body.install.vehicleName = nombre;
    const asunto = txt(inst.issueLabel);
    if (asunto) body.install.issueLabel = asunto;
  }

  return body;
}

/**
 * El acuse. `created:false` NO es un error: es el reintento contestando que el
 * pedido ya estaba hecho. La pantalla tiene que poder distinguirlos para no
 * decir "listo, pedido nuevo" dos veces por la misma compra.
 */
export function adaptOrderAck(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};
  return {
    id: p.id ?? null,
    created: p.created !== false,
    status: minusculas(p.status) || 'pending',
    paymentStatus: minusculas(p.paymentStatus),
    deliveryMethod: minusculas(p.deliveryMethod),
    subtotal: pesosOcero(p.subtotalCents),
    installationTotal: pesosOcero(p.installationTotalCents),
    deliveryFee: pesosOcero(p.deliveryFeeCents),
    total: pesosOcero(p.totalCents),
    itemCount: Number(p.itemCount) || 0,
    installationAppointmentId: p.installationAppointmentId ?? null,
    createdAt: ts(p.createdAt),
  };
}

/**
 * CREA EL PEDIDO. Los errores SUBEN con su `ApiError`: quien llama tiene que
 * poder distinguir "no hay inventario" (decidido) de "se cayó la red" (no se
 * sabe), y esa diferencia es la que impide el cobro doble.
 */
export async function createOrder(body, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  return adaptOrderAck(await call('/orders', { method: 'POST', body, signal }));
}

// ────────────────────────────────────────────────────────────────────────────
// LEER
// ────────────────────────────────────────────────────────────────────────────

/** Un pedido de la LISTA. Sin líneas: esas viven en la ficha. */
export function adaptOrderSummary(row) {
  if (!row || typeof row !== 'object') return null;
  const total = pesosOcero(row.totalCents);
  const reembolso = pesos(row.refundAmountCents);
  return {
    id: row.id,
    clientId: row.clientId ?? null,
    workshopId: row.workshopId ?? null,
    workshopName: row.workshopName ?? null,
    status: minusculas(row.status) || 'pending',
    paymentMethod: row.paymentMethod ?? null,
    paymentStatus: minusculas(row.paymentStatus),
    deliveryMethod: minusculas(row.deliveryMethod),
    subtotal: pesosOcero(row.subtotalCents),
    installationTotal: pesosOcero(row.installationTotalCents),
    deliveryFee: pesosOcero(row.deliveryFeeCents),
    total,
    // 🔴 `null` ≠ 0. Ver la trampa nº 5.
    refundAmount: reembolso,
    net: pesos(row.netCents) ?? total - (reembolso ?? 0),
    returnStatus: minusculas(row.returnStatus),
    createdAt: ts(row.createdAt),
    completedAt: ts(row.completedAt),
    cancelledAt: ts(row.cancelledAt),
  };
}

/** Una línea del pedido, con los nombres que la pantalla ya pinta. */
function adaptItem(i) {
  return {
    lineNo: Number(i.lineNo) || 0,
    productId: i.productId ?? null,
    productName: i.productName ?? '',
    variationId: i.variationId ?? null,
    variationName: i.variationName ?? null,
    quantity: Number(i.quantity) || 0,
    unitPrice: pesosOcero(i.unitPriceCents),
    installationRequested: i.installationRequested === true,
    installationPrice: pesosOcero(i.installationPriceCents),
    lineTotal: pesosOcero(i.lineTotalCents),
    // La pantalla lee `item.photo`; el API lo llama `photoUrl`. Sin este puente
    // todas las miniaturas del historial caen al icono genérico.
    photo: i.photoUrl ?? null,
    category: i.category ?? null,
  };
}

/** LA FICHA: el resumen + las líneas + el membrete congelado del taller. */
export function adaptOrderDetail(row) {
  const base = adaptOrderSummary(row);
  if (!base) return null;
  const lineas = Array.isArray(row.items) ? row.items.map(adaptItem) : [];
  const cuentas = Array.isArray(row.transferAccounts) ? row.transferAccounts : [];
  return {
    ...base,
    clientName: row.clientName ?? '',
    clientPhone: row.clientPhone ?? '',
    workshopPhone: row.workshopPhone ?? null,
    workshopAddress: row.workshopAddress ?? null,
    workshopLogoURL: row.workshopLogoUrl ?? null,
    deliveryAddress: row.deliveryAddress ?? null,
    notes: row.notes ?? null,
    returnReason: row.returnReason ?? null,
    installationAppointmentId: row.installationAppointmentId ?? null,
    items: lineas,
    transferAccounts: cuentas.map((c) => ({
      bank: c.bank ?? '',
      accountNumber: c.accountNumber ?? '',
      accountHolder: c.accountHolder ?? '',
    })),
  };
}

/**
 * La respuesta de la lista. Si no viene una lista, LANZA: una lista vacía es una
 * afirmación ("no has comprado nada") y este módulo no está en condiciones de
 * hacerla cuando lo que pasó fue que no se pudo preguntar.
 */
export function adaptOrderPage(payload, { page = 1, pageSize = ORDERS_PAGE_SIZE } = {}) {
  const lista = payload && (payload.items ?? payload.orders);
  if (!Array.isArray(lista)) {
    throw new ApiError('El API no devolvió una lista de pedidos.', { code: 'bad_response' });
  }
  const crudo = payload.total ?? payload.totalCount;
  const total = crudo === null || crudo === undefined || !Number.isFinite(Number(crudo)) ? null : Number(crudo);
  const tam = Number(payload.pageSize) || pageSize;
  const paginasDelServidor = Number(payload.totalPages);

  return {
    orders: lista.map(adaptOrderSummary).filter(Boolean),
    total,
    totalPages: Number.isFinite(paginasDelServidor) && paginasDelServidor > 0
      ? paginasDelServidor
      : (total === null ? null : Math.max(1, Math.ceil(total / tam))),
    page: Number(payload.page) || page,
    pageSize: tam,
  };
}

/**
 * MIS COMPRAS.
 *
 * 🔴 `scope=client` NO es opcional. Sin él, el API devuelve todo lo que uno
 * puede ver, y el dueño de un taller vería los pedidos que le HAN HECHO mezclados
 * con los que él compró, en la pantalla que se llama "Mis pedidos". No daría
 * ningún error: daría pedidos ajenos con botón de cancelar.
 */
export async function fetchOrders({ page = 1, pageSize = ORDERS_PAGE_SIZE, status } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const p = Math.min(ORDERS_MAX_PAGE, Math.max(1, Math.floor(Number(page) || 1)));
  const query = { scope: 'client', page: p, pageSize };
  if (status) query.status = String(status).toUpperCase();
  const payload = await call('/orders', { query, signal });
  return adaptOrderPage(payload, { page: p, pageSize });
}

/**
 * LA FICHA de un pedido.
 *
 * ⚠️ EL 404 NO ES UN ERROR, ES UNA RESPUESTA: el API contesta 404 tanto si el
 * pedido no existe como si no es tuyo (distinguirlos le diría a cualquiera qué
 * números de pedido existen). Cualquier OTRO fallo sí sube.
 */
export async function fetchOrder(id, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    return adaptOrderDetail(await call(`/orders/${encodeURIComponent(id)}`, { signal }));
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// MOVER
// ────────────────────────────────────────────────────────────────────────────

/**
 * CANCELAR. Ruta propia y cuerpo mínimo a propósito: un PATCH genérico sobre el
 * pedido sería la puerta por la que se reescriben los importes.
 */
export async function cancelOrder(id, { note, apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = { status: 'CANCELLED' };
  const nota = txt(note);
  if (nota) body.note = nota;
  const r = await call(`/orders/${encodeURIComponent(id)}/status`, { method: 'PATCH', body, signal });
  return {
    id: r?.id ?? id,
    status: minusculas(r?.status) || 'cancelled',
    previousStatus: minusculas(r?.previousStatus),
    stockRestored: r?.stockRestored === true,
  };
}

/**
 * MOVER EL PEDIDO — la que usan el taller y el admin.
 *
 * `cancelOrder` es el caso del cliente y por eso lleva el estado dentro; ésta es
 * la general. Comparten ruta a propósito: `PATCH /orders/:id/status` es lo ÚNICO
 * que mueve un pedido, y no acepta ni un importe ni una línea. Quién puede
 * llegar a qué estado lo decide el servidor por el papel de quien llama
 * (`transicionesDe`), así que la pantalla no tiene que replicar esa tabla: si el
 * salto no cabe, el 409 lo dice con las palabras exactas ("desde aquí solo cabe:
 * …").
 *
 * ⚠️ Los estados van en MAYÚSCULAS en la base y en minúsculas en la app; la
 * traducción vive aquí, en los dos sentidos.
 */
export async function changeOrderStatus(id, status, { note, apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const estado = String(status || '').trim().toUpperCase();
  if (!estado) throw new OrderDraftError('status: hay que decir a qué estado se mueve.', 'no_status');
  const body = { status: estado };
  const nota = txt(note);
  if (nota) body.note = nota;
  const r = await call(`/orders/${encodeURIComponent(id)}/status`, { method: 'PATCH', body, signal });
  return {
    id: r?.id ?? id,
    status: minusculas(r?.status) || estado.toLowerCase(),
    previousStatus: minusculas(r?.previousStatus),
    stockRestored: r?.stockRestored === true,
  };
}

/**
 * REGISTRAR EL COBRO — y, si se pide, CERRAR el pedido en la MISMA transacción.
 *
 * 🔴 POR QUÉ `complete` VIAJA AQUÍ Y NO EN UNA SEGUNDA LLAMADA. La pantalla del
 * taller mueve cobro y cierre en un solo botón ("Pago confirmado al completar").
 * Partirlo en dos peticiones deja un hueco real: la segunda que se cae por red
 * deja el pedido COMPLETADO Y SIN COBRAR, y nadie se entera hasta que cuadran
 * caja. El servidor las hace en una transacción: o las dos, o ninguna.
 *
 * ⚠️ Y las valida en ese orden: si el cierre NO cabe desde el estado actual, el
 * cobro NI SE INTENTA (409 con las palabras exactas). Es lo contrario de lo que
 * hace hoy el camino de Firestore, que escribe `status:'completed'` encima de
 * cualquier estado sin preguntar.
 *
 * `paymentChanged:false` NO es un error: significa que ya estaba cobrado, o sea
 * que el reintento encontró su propio trabajo hecho.
 *
 * El cobro no se deshace: `status` sólo admite `PAID`.
 */
export async function markOrderPaid(id, { complete = false, note, apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = { status: 'PAID' };
  if (complete === true) body.completeOrder = true;
  const nota = txt(note);
  if (nota) body.note = nota;
  const r = await call(`/orders/${encodeURIComponent(id)}/payment`, { method: 'PATCH', body, signal });
  return {
    id: r?.id ?? id,
    paymentStatus: minusculas(r?.paymentStatus) || 'paid',
    paidAt: ts(r?.paidAt),
    paymentChanged: r?.paymentChanged === true,
    status: minusculas(r?.status),
    previousStatus: minusculas(r?.previousStatus),
    statusChanged: r?.statusChanged === true,
  };
}

/**
 * RESOLVER la devolución (aprobar o rechazar). Es del negocio o del admin.
 *
 * 🔴 Aprobar NO es solo cambiar una etiqueta: el servidor devuelve la mercancía
 * al inventario Y deja escrito el monto del reembolso, en la misma transacción.
 * Por eso el cliente no puede auto-aprobársela, y por eso esta llamada no se
 * puede sustituir por un `updateDoc` de `returnStatus`.
 */
export async function resolveReturn(id, approved, { note, apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const body = { status: approved ? 'APPROVED' : 'REJECTED' };
  const nota = txt(note);
  if (nota) body.note = nota;
  const r = await call(`/orders/${encodeURIComponent(id)}/return`, { method: 'PATCH', body, signal });
  return {
    id: r?.id ?? id,
    returnStatus: minusculas(r?.returnStatus) || (approved ? 'approved' : 'rejected'),
    refundAmount: pesos(r?.refundAmountCents),
    stockRestored: r?.stockRestored === true,
  };
}

/** PEDIR una devolución. Resolverla es del negocio; esta ruta es la del cliente. */
export async function requestReturn(id, reason, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const motivo = txt(reason);
  if (!motivo) throw new OrderDraftError('reason: hay que decir por qué se devuelve.', 'no_reason');
  const r = await call(`/orders/${encodeURIComponent(id)}/return`, { method: 'POST', body: { reason: motivo }, signal });
  return {
    id: r?.id ?? id,
    returnStatus: minusculas(r?.returnStatus) || 'requested',
    refundAmount: pesos(r?.refundAmountCents),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// LOS FALLOS
// ────────────────────────────────────────────────────────────────────────────

/** Cuánto del motivo cabe en un aviso sin volverse ilegible. */
const MAX_MOTIVO = 240;

/**
 * EL MOTIVO REAL DEL RECHAZO, para enseñárselo a quien compra.
 *
 * 🔴 El API contesta 400 NOMBRANDO el campo y 409 NOMBRANDO el producto y las
 * unidades que quedan. Tragarse eso y decir "Error" deja al cliente adivinando
 * delante de un carrito que no puede pagar; y decir "Pedido confirmado" cuando
 * el servidor dijo que no es peor, y tiene antecedentes en este proyecto.
 *
 * Solo se enseña lo que viene del API (`ApiError`) o lo que rechazamos nosotros a
 * conciencia (`OrderDraftError`). Un `TypeError` nuestro NO: eso es un fallo de
 * programación y disfrazarlo de respuesta del servidor manda al cliente a
 * arreglar un carrito que estaba bien.
 */
export function orderErrorReason(err) {
  if (!err || typeof err.message !== 'string') return null;
  if (err.name !== 'ApiError' && err.name !== 'OrderDraftError') return null;
  const msg = err.message.trim();
  if (!msg) return null;
  return msg.length > MAX_MOTIVO ? `${msg.slice(0, MAX_MOTIVO - 1)}…` : msg;
}

/** El 409 tiene su propia cara: no es "corrige lo que mandaste", es "quedaban dos". */
export function isStockConflict(err) {
  return !!err && (err.status === 409 || err.code === 'conflict');
}

/**
 * 🔴 ¿SE COBRÓ O NO SE COBRÓ?
 *
 * Esta función es la que decide qué se le dice al cliente cuando algo sale mal, y
 * es la diferencia entre un cliente tranquilo y un cobro doble.
 *
 *  · El servidor CONTESTÓ (400/403/404/409) → decidió, y la transacción hizo
 *    ROLLBACK: no hay pedido. Se puede decir "no se completó" sin mentir.
 *  · No hubo respuesta (red, plazo vencido, petición cancelada, un 200 que no es
 *    JSON, un 500) → NO SE SABE. La petición pudo llegar entera y el pedido pudo
 *    quedar creado. Aquí NO se puede decir "falló": hay que reintentar CON LA
 *    MISMA CLAVE, que es lo que convierte el reintento en una pregunta.
 *  · `not_configured` / `auth_unavailable` → la petición nunca salió del
 *    navegador. Tampoco hay duda.
 */
const SIN_RESPUESTA = new Set(['network', 'timeout', 'aborted', 'bad_response']);

export function isInconclusive(err) {
  if (!err) return false;
  if (SIN_RESPUESTA.has(err.code)) return true;
  return Number(err.status) >= 500;
}

/**
 * ─── LOS PEDIDOS QUE RECIBE EL NEGOCIO ──────────────────────────────────────
 *
 * 🔴 `scope=workshop` NO es opcional, y por el motivo simétrico al de
 * `fetchOrders`: sin él el API devuelve TODO lo que uno puede ver, y el dueño
 * vería en "Pedidos recibidos" los que él mismo hizo como comprador, con los
 * botones de despachar puestos. No daría ningún error: daría pedidos ajenos al
 * negocio con acciones de negocio encima.
 *
 * ⚠️ El API contesta 400 si quien llama no pertenece a ningún negocio. Es
 * correcto y hay que dejarlo subir: una lista vacía ahí diría "no te han
 * comprado nada" cuando lo que pasa es que la cuenta no tiene taller.
 */
export async function fetchWorkshopOrders({ page = 1, pageSize = ORDERS_PAGE_SIZE, status } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const p = Math.min(ORDERS_MAX_PAGE, Math.max(1, Math.floor(Number(page) || 1)));
  const query = { scope: 'workshop', page: p, pageSize };
  if (status) query.status = String(status).toUpperCase();
  const payload = await call('/orders', { query, signal });
  return adaptOrderPage(payload, { page: p, pageSize });
}

/**
 * TODOS LOS PEDIDOS — la cola del admin.
 *
 * Sin `scope`: el API deja el `WHERE` vacío cuando quien pregunta es admin, y
 * recorta a lo suyo cuando no lo es. O sea que esta misma llamada NO es una
 * puerta trasera: a un no-admin le devuelve exactamente lo que ya podía ver.
 */
export async function fetchAllOrders({ page = 1, pageSize = ORDERS_PAGE_SIZE, status } = {}, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const p = Math.min(ORDERS_MAX_PAGE, Math.max(1, Math.floor(Number(page) || 1)));
  const query = { page: p, pageSize };
  if (status) query.status = String(status).toUpperCase();
  const payload = await call('/orders', { query, signal });
  return adaptOrderPage(payload, { page: p, pageSize });
}

/**
 * ─── EL CARRITO ─────────────────────────────────────────────────────────────
 *
 * 🔴 LO QUE ESTA COPIA ES Y LO QUE NO ES. El dato de verdad del carrito vive en
 * `localStorage`: la app nunca lo relee del servidor, y por eso el carrito no
 * sigue al usuario entre dispositivos. Esta copia existe para UNA sola cosa: que
 * el recordatorio de carrito abandonado pueda verlo desde el servidor.
 *
 * ⚠️ Y HOY ESA COPIA NO LA LEE NADIE, lo cual conviene decir para que nadie la
 * dé por viva. `checkAbandonedCarts` (`functions/src/cart.js`) sí consulta el
 * interruptor y **se omite entera** cuando `orders` se lee de Postgres: sin ver
 * los pedidos no puede contestar «¿compró después de dejar esto aquí?», y el
 * aviso le llegaría justo a quien acaba de comprar. Se apaga con constancia en
 * el registro, no en silencio. Estas dos funciones existen para que el dato esté
 * cuando alguien construya ese barrido sobre el API, porque un carrito que no se
 * copió durante meses no se recupera después.
 *
 * NO se mandan importes. El carrito de la pantalla lleva `unitPrice`,
 * `installationPrice`, `productName` y `photo` porque los pinta; el servidor
 * guarda la lista tal cual como borrador, pero quien decide lo que se cobra es
 * `POST /orders`, que recalcula TODO desde el catálogo y no acepta ni un precio.
 * Esta ruta no crea pedidos ni mueve inventario.
 */

/** Guarda el borrador. NUNCA lanza: perder la copia no puede romper el carrito. */
export async function saveCart(items, { apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  const lista = Array.isArray(items) ? items : [];
  try {
    await call('/cart', { method: 'PUT', body: { items: lista }, signal });
    return true;
  } catch {
    // Igual que el `.catch(() => {})` del camino de Firestore: el carrito que el
    // cliente ve es el de `localStorage` y ya está guardado. Tumbar la pantalla
    // por no poder copiarlo sería cambiar una compra por un dato de marketing.
    return false;
  }
}

/**
 * Lee el borrador guardado. Hoy NADIE lo llama —la app se hidrata de
 * `localStorage`—, y está aquí porque el endpoint es un par: dejar solo la
 * escritura convierte la ruta en un buzón sin destinatario y el próximo que
 * quiera el carrito entre dispositivos escribiría su propio `fetch`.
 * Devuelve `null` cuando no hay carrito guardado (404), que no es un error.
 */
export async function fetchCart({ apiFetchImpl, signal } = {}) {
  const call = apiFetchImpl || apiFetch;
  try {
    const payload = await call('/cart', { signal });
    const lista = payload && payload.items;
    return {
      items: Array.isArray(lista) ? lista : [],
      updatedAt: ts(payload?.updatedAt),
    };
  } catch (err) {
    if (err && (err.status === 404 || err.code === 'not_found')) return null;
    throw err;
  }
}
